import type { HelpCategory } from "../../types";

export const menu: HelpCategory = {
  id: "menu",
  title: "Меню и себестоимость",
  description: "Категории, позиции, модификаторы, стоп-лист, продукты, техкарты и калькуляция.",
  icon: "utensils",
  articles: [
    {
      id: "menu-items",
      title: "Меню: категории, позиции, варианты, модификаторы, стоп-лист",
      summary: "Как собрать меню: категории до трёх уровней, позиции с ценой и фото, размеры (варианты), добавки (модификаторы), цены по филиалам и стоп-лист.",
      path: "/menu",
      feature: "core.menu",
      permission: "menu.view",
      keywords: ["меню", "блюдо", "позиция", "категория", "модификатор", "добавка", "размер", "вариант", "стоп-лист", "цена", "на вес", "весовой", "килограмм"],
      related: ["recipes", "costing", "kds", "site-qr"],
      body: [
        { t: "video", id: "menu-item", title: "Новое блюдо в меню", duration: 26 },
        {
          t: "steps", title: "Категории",
          items: [
            "[«Меню»](/menu) → вкладка «Категории» → **«Новая категория»**.",
            "Категория может быть вложенной (до 3 уровней): «Напитки → Кофе → Холодный кофе».",
            "Порядок перетаскивается — так категории идут на кассе и на сайте.",
          ],
        },
        {
          t: "steps", title: "Позиция меню",
          items: [
            "Вкладка «Позиции» → **«Новая позиция»**: категория, название, тип (блюдо, напиток, товар, услуга), цена, фото.",
            "**Тип станции кухни** — на какой экран уйдёт блюдо (горячий цех, бар…). См. [[kds]].",
            "**Варианты** — размеры с отдельной ценой (S/M/L, 0,3/0,5). Один вариант помечается «по умолчанию».",
            "**Группы модификаторов** — добавки и замены (сироп, «без лука»). Сами группы создаются во вкладке «Модификаторы».",
            "**Разрешены скидки** — можно ли давать скидку на эту позицию.",
            "**В продаже** — выключенная позиция не продаётся нигде; **Видна гостям** — показывать ли в QR-меню и на сайте.",
            "**Цена в филиале** — базовая цена общая, в филиале можно задать свою или скрыть позицию (пустое поле — базовая цена).",
            "**Складской продукт** — для товаров без техкарты (вода, кола): при продаже спишется сам продукт.",
            "Для алкоголя и табака — **возрастное ограничение**: кассир подтвердит возраст гостя.",
          ],
        },
        {
          t: "steps", title: "Продажа на вес",
          items: [
            "Для плова, рыбы, салатов на развес: в карточке позиции включите **«Продажа на вес»** — цена станет **ценой за 1 кг** (на кассе и сайте — «1 200 сом/кг»).",
            "На кассе при нажатии на позицию откроется окно веса: введите вес в **граммах или килограммах** (переключатель «г / кг») или нажмите быструю кнопку — 100 г, 300 г, 1 кг. Сумма считается сразу.",
            "В заказе строка показывает вес: «450 г × 1 200 сом/кг». Пока позиция не ушла на кухню, нажмите на вес, чтобы **изменить его**.",
            "Кухня, чек и пречек показывают вес («Плов · 450 г»). В QR-меню гость выбирает примерный вес — точную сумму посчитают после взвешивания.",
            "**Техкарта** весовой позиции — на килограммы: казан плова на 12 кг — выход 12; продажа 450 г спишет 0,45 / 12 продуктов казана.",
          ],
        },
        {
          t: "terms", title: "Модификаторы",
          items: [
            { term: "Группа", text: "Например «Сироп»: минимум и максимум выбора (0–1 — можно не выбирать; 1–1 — обязательно один)." },
            { term: "Модификатор с продуктом", text: "«Сироп ванильный +30 сом, 10 мл» — при продаже спишет 10 мл сиропа." },
            { term: "«Без X»", text: "Убирает продукт X из техкарты блюда при продаже — склад не спишет его." },
            { term: "«Замена X на Y»", text: "Вместо X спишется Y (например, растительное молоко вместо обычного)." },
          ],
        },
        {
          t: "steps", title: "Стоп-лист",
          items: [
            "Вкладка «Стоп-лист» → **«Поставить на стоп»**: позиция и, при желании, **остаток порций** (например, «осталось 5»).",
            "Касса и сайт сразу перестанут продавать позицию. С остатком — продажи уменьшают его, на нуле позиция встаёт на стоп сама.",
            "Снимите со стопа, когда блюдо снова есть.",
          ],
        },
        {
          t: "steps", title: "Изменить сразу несколько",
          items: [
            "Вкладка «Позиции» → **«Выбрать»** и отметьте карточки (можно из разных категорий и страниц) или **«Выбрать все на странице»**.",
            "Внизу появится панель: **«Изменить»** — продажа, публичное меню, скидка, перенос в категорию, цена на % или на сумму (вместе с вариантами); **«Стоп-лист»** — поставить на стоп или вернуть в продажу; **«Удалить»**.",
            "В «Категориях» и «Стоп-листе» отметьте строки флажками: категории можно скрыть или показать, поменять каналы продаж, удалить пустые; в стоп-листе — вернуть в продажу или задать остаток.",
          ],
        },
        { t: "tip", text: "Цена в меню — для гостя. Себестоимость считается из техкарты: см. [[recipes]] и [[costing]]." },
      ],
    },
    {
      id: "products",
      title: "Продукты: что закупаем и храним",
      summary: "Справочник продуктов склада: ингредиенты, товары, полуфабрикаты, упаковка — с единицей учёта, минимальным остатком и сроком хранения.",
      path: "/products",
      feature: "core.recipes",
      permission: "recipes.view",
      keywords: ["продукт", "ингредиент", "сырьё", "единица", "минимальный остаток", "срок хранения", "полуфабрикат"],
      related: ["recipes", "purchases", "stock-balances", "production"],
      body: [
        {
          t: "terms", title: "Типы продуктов",
          items: [
            { term: "Ингредиент", text: "Сырьё для блюд: мука, молоко, мясо." },
            { term: "Товар", text: "Продаётся как есть: вода, кола, шоколадка." },
            { term: "Полуфабрикат (ПФ)", text: "Готовится у вас по своей техкарте: тесто, соус, бульон. См. [[production]]." },
            { term: "Упаковка и расходники", text: "Стаканы, крышки, салфетки." },
          ],
        },
        {
          t: "terms", title: "Важные поля",
          items: [
            { term: "Базовая единица", text: "г, мл или шт. В ней ведётся склад. Закупать можно в кг, л, упаковках — система переведёт сама. Менять единицу после движений нельзя." },
            { term: "Плотность", text: "Сколько граммов в 1 мл — нужна, если в техкарте продукт в мл, а склад в г (масло, мёд)." },
            { term: "Минимальный остаток", text: "Ниже него продукт помечается «Мало», и приходит уведомление." },
            { term: "Срок хранения, дней", text: "С расширенным складом срок годности партии при закупке подставится сам." },
            { term: "Отход %", text: "Потери при чистке по умолчанию — подставляется в техкарты." },
            { term: "Последняя цена закупки", text: "Обновляется при каждой закупке; от неё считается себестоимость блюд." },
          ],
        },
      ],
    },
    {
      id: "recipes",
      title: "Техкарты (ТТК): состав блюда и списание со склада",
      summary: "Техкарта говорит, сколько каких продуктов уходит на одну порцию. Без неё склад не списывает продукты при продаже, а себестоимость равна нулю.",
      path: "/menu",
      feature: "core.recipes",
      permission: "recipes.view",
      keywords: ["ттк", "техкарта", "рецепт", "брутто", "нетто", "выход", "состав", "калькуляционная карта"],
      related: ["menu-items", "products", "costing", "production"],
      body: [
        { t: "video", id: "recipe", title: "Как составить и опубликовать техкарту", duration: 43 },
        {
          t: "steps", title: "Создать техкарту блюда",
          items: [
            "Откройте позицию в [«Меню»](/menu) → вкладка **«ТТК»** (для варианта — у каждого варианта своя).",
            "Добавьте строки: продукт или полуфабрикат, **брутто** и единица (г, кг, мл, шт).",
            "Укажите **отход %** (чистка) и **потери %** (при готовке) — система посчитает **нетто**.",
            "Строки «только с собой» (стакан, крышка) отметьте — они спишутся только для заказов навынос и доставки.",
            "Проверьте себестоимость порции справа и нажмите **«Опубликовать ТТК»**.",
          ],
        },
        {
          t: "terms", title: "Термины",
          items: [
            { term: "Брутто", text: "Сколько продукта берёте со склада (до чистки)." },
            { term: "Нетто", text: "Сколько остаётся после отходов и потерь — информационно." },
            { term: "Выход", text: "Для блюда — 1 порция. Для полуфабриката — сколько граммов/мл получается (например, 1000 г теста)." },
            { term: "Версии", text: "Опубликованная версия списывает продажи. Изменения делаются в черновике; после публикации старая версия уходит в архив, прошлые заказы не меняются." },
          ],
        },
        { t: "tip", text: "У каждого варианта (размера) своя ТТК. Если у варианта её нет — при продаже спишется общая ТТК блюда. ТТК одного варианта можно скопировать в другие с коэффициентом (например, ×1,5 для L)." },
        {
          t: "faq",
          items: [
            { q: "Себестоимость показывает «неполная»", a: "У какого-то продукта нет цены закупки (не было закупок) или единица не переводится (мл ↔ г без плотности). Проведите закупку или заполните плотность продукта." },
            { q: "Полуфабрикат в техкарте", a: "Добавьте продукт типа «Полуфабрикат» (у него своя ТТК). При продаже спишется сырьё по его ТТК, а с модулем «Производство» — готовый полуфабрикат со склада." },
            { q: "«Циклическая ссылка»", a: "Полуфабрикат не может ссылаться сам на себя через другие ПФ." },
          ],
        },
      ],
    },
    {
      id: "costing",
      title: "Калькуляция: себестоимость, food cost, наценка",
      summary: "Сколько стоит порция по техкарте, какой процент себестоимости в цене (food cost) и какую цену рекомендует система.",
      path: "/costing",
      feature: "core.recipes",
      permission: "costing.view",
      keywords: ["себестоимость", "food cost", "фудкост", "наценка", "маржа", "калькуляция"],
      related: ["recipes", "insights"],
      body: [
        { t: "p", text: "[«Калькуляция»](/costing) показывает все позиции меню: цену, себестоимость по опубликованной ТТК, **food cost %** = себестоимость ÷ цена × 100, маржу и **рекомендуемую цену** под ваш целевой food cost." },
        {
          t: "steps", title: "Настроить",
          items: ["«Настройки калькуляции»: **целевой food cost** (обычно 25–35%) и **округление цены** (например, до 5 сом).", "Считать от брутто или нетто — как принято у вас."],
        },
        { t: "tip", text: "Себестоимость берётся из последней цены закупки продуктов. Подорожала мука — проведите закупку по новой цене, и калькуляция пересчитается." },
      ],
    },
    {
      id: "menu-packs",
      title: "Готовые меню: наборы с фото и техкартами",
      summary: "Купить готовое меню под свой формат (шаурмичная, кофейня, кафе-мороженое, национальная кухня и другие) и загрузить его в своё меню: блюда с фото и описаниями, продукты, полуфабрикаты и техкарты.",
      path: "/menu-packs",
      feature: "core.menu",
      permission: "menu.view",
      keywords: ["готовое меню", "шаблон меню", "набор", "техкарты", "фото блюд", "шаурма", "кофейня", "мороженое"],
      related: ["menu-items", "recipes", "costing"],
      body: [
        { t: "p", text: "[«Готовые меню»](/menu-packs) — наборы под формат заведения: **шаурмичная, кофейня, кафе-мороженое, кыргызская кухня, узбекская чайхана, бургерная, пиццерия, суши-бар, грузинская кухня, пекарня, блинная, столовая, бабл-ти**. В каждом наборе — 20–30 блюд с фото и описанием, продукты с ценами закупки, полуфабрикаты (соусы, тесто, маринады) и опубликованные техкарты с выходом, отходами и потерями." },
        { t: "steps", title: "Купить", items: ["Откройте набор и посмотрите блюда: цену, себестоимость и food cost, состав. Граммовки и технология видны после покупки.", "Нажмите **«Купить»** — появится счёт с реквизитами. Его можно распечатать; все счета — в [«Подписке»](/settings/subscription).", "После оплаты набор откроется сам. Купить может владелец или администратор."] },
        { t: "steps", title: "Загрузить в меню", items: ["Нажмите **«Загрузить в меню»**: весь набор или только нужные блюда.", "Выберите, загружать ли фото и техкарты. **«Скрыть позиции до проверки»** — блюда появятся выключенными, включите их в [[menu-items]], когда проверите цены.", "Готово: в меню — категории и позиции с размерами и модификаторами, в «Продуктах» — продукты и полуфабрикаты, в «Калькуляции» — себестоимость."] },
        { t: "tip", text: "Загружать можно сколько угодно раз: блюда с тем же названием пропускаются, продукты, категории и группы модификаторов с тем же названием используются повторно — дублей не будет." },
        { t: "warn", text: "Цены закупки в наборе — ориентир по Бишкеку. После загрузки проведите закупку по своим ценам — [[costing]] пересчитается. Цены блюд меняйте под свой рынок." },
        { t: "faq", items: [{ q: "Можно поменять блюда после загрузки?", a: "Да, это обычные позиции и техкарты вашего меню — меняйте названия, цены, граммовки и фото как обычно." }, { q: "Откуда фото?", a: "Фото — из Wikimedia Commons со свободными лицензиями; автор и лицензия указаны в карточке блюда. Можно заменить на свои в позиции меню." }] },
      ],
    },
  ],
};
